<script lang="ts">
	import { Svg } from '@vpap_svelte/ui';
	import { mergeAttributes } from '@vpap_svelte/internal';

	import type { HTMLElementProps } from '@vpap_svelte/internal';

	interface Props extends HTMLElementProps<HTMLDivElement> {
		/**
		 * The x position of the tooltip (i.e. style:left)
		 */
		x: number;
		/**
		 * The y-position of the tooltip (i.e. style:top).
		 */
		y: number;
		/**
		 * Whether the tooltip is currently visible
		 */
		visible: boolean;
		/** 
  		Where the tooltip is positioned. "absolute" means it is positioned with `position: absolute`.
  		"fixed" means it is positioned with "position: fixed". 
  		*/
		position?: 'absolute' | 'fixed' | 'fixed-bottom';
		/**
		 * Whether the tooltip is presented to screen-reader users
		 */
		srVisible?: boolean;
	}

	let {
		x,
		y,
		visible = $bindable(),
		children,
		position = 'fixed',
		srVisible = true,
		...attrs
	}: Props = $props();

	let requiredProps: HTMLElementProps<HTMLDivElement> = $derived.by(() => {
		return {
			'aria-hidden': srVisible === false ? true : undefined,
			role: 'tooltip',
			class: ['data-tooltip', position],
			style: {
				left: position === 'fixed-bottom' ? undefined : `${x}px`,
				top: position === 'fixed-bottom' ? undefined : `${y}px`,
				display: !visible ? 'none' : undefined,
			},
			// often, we'll hide the tooltip when a user initiates a mouseleave/pointerexit/etc event
			onmouseover: () => {
				visible = true;
			},
			onfocus: () => {
				visible = true;
			},
		};
	});

	let containerAttrs = $derived(mergeAttributes({ ...attrs }, { ...requiredProps }));
</script>

<div {...containerAttrs}>
	{#if srVisible}
		<span class="sr-only">Tooltip:</span>
	{/if}
	{#if position === 'fixed-bottom'}
		<button
			type="button"
			onclick={() => {
				visible = false;
			}}
			aria-label="Close Tooltip"
		>
			<Svg width="50%" height="50%" viewBox="0 0 18 18" role="presentation">
				<line x1="0" x2="18" y1="0" y2="18" stroke-width="2" stroke="#42585c" />
				<line x1="18" y1="0" x2="0" y2="18" stroke-width="2" stroke="#42585c" />
			</Svg>
		</button>
	{/if}
	{#if children}
		{@render children()}
	{/if}
</div>

<style>
	.sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border-width: 0;
	}

	div.data-tooltip {
		border-color: #eee;
		background-color: #fff;
	}

	div.data-tooltip.fixed,
	div.data-tooltip.fixed-bottom {
		position: fixed;
	}

	div.data-tooltip.absolute {
		position: absolute;
	}

	div.data-tooltip.fixed-bottom {
		bottom: 0;
		left: 0;
		width: 100%;
	}

	div.data-tooltip:not(.fixed-bottom) {
		pointer-events: none;
		border-radius: 0.25rem;
		border-width: 1px;
		box-shadow:
			0 10px 15px -3px #0000001a,
			0 4px 6px -4px #0000001a;
	}

	button {
		position: absolute;
		right: 1px;
		top: 0;
		width: 2rem;
		height: 2rem;
		font-family: inherit; /* 1 */
		font-feature-settings: inherit; /* 1 */
		font-variation-settings: inherit; /* 1 */
		font-size: 100%; /* 1 */
		font-weight: inherit; /* 1 */
		line-height: inherit; /* 1 */
		color: inherit; /* 1 */
		margin: 0; /* 2 */
		padding: 0; /* 3 */
		text-transform: none;
		background-color: transparent; /* 2 */
		background-image: none; /* 2 */
		cursor: pointer;
		border: none;
	}
</style>
